<template>

  <v-container fluid class="mt-0 page page-org-profile-edit">

    <!-- Breadcrumb -->
    <v-row class="mt-4">
      <v-col cols="12" class="d-flex align-center justify-start page-breadcrumb-new">
        <!-- (breadcrumb) -->
        <v-icon small class="mr-2" v-if="uiType === 'sys-admin'">mdi-shield-account</v-icon>
        <v-icon small class="mr-2" v-else>mdi-account-circle-outline</v-icon>
        <router-link :to="{ name: 'org-profile' }">Org Profile</router-link>
        <div class="separator">/</div>
        <div class="active-item"><span class="label-prefix">Edit Org:</span> {{ activeOrg.name }}</div>
      </v-col>
    </v-row>

    <!-- Page Actions (close, cancel, save) -->
    <v-row class="mt-10 mb-4">
      <v-col cols="6" class="pl-8 pt-0">
        <v-btn
            v-if="isDirty"
            color="gray"
            width="100"
            :disabled="isOrgInfoSaving"
            @click="cancel">
          Cancel
        </v-btn>
        <div class="changed-message-text" v-else-if="orgInfoSavedMessage">
          <v-icon color="green" class="mr-2 pb-1">mdi-check-bold</v-icon>
          <span>{{ orgInfoSavedMessage }}</span>
        </div>
      </v-col>
      <v-col cols="6" class="pr-8 pt-0" align="right">
        <v-btn
            v-if="isDirty"
            color="primary"
            width="110"
            :disabled="isOrgInfoSaving"
            @click="saveOrgInfo">
          Save
        </v-btn>
        <v-btn
            v-else
            color="gray"
            width="110"
            @click="close">
          Close
        </v-btn>
      </v-col>
    </v-row>

    <!-- (data saving) -->
    <template v-if="isOrgInfoSaving">
      <v-row>
        <v-col class="mt-4 d-flex align-center justify-center" style="height:120px">
          <v-progress-circular indeterminate :size="50" style="margin:20px 0" color="primary" />
        </v-col>
      </v-row>
    </template>

    <!-- Edit Profile Info -->
    <v-container fluid class="pt-4 pb-9 page-content" v-else-if="orgToEdit">
      <!-- Org info title -->
      <v-row class="pa-0 ma-0">
        <v-col cols="12">
          <span class="title">Org Info</span>
        </v-col>
      </v-row>

      <!-- First name -->
      <v-row align="center" class="pa-0 ma-0 mt-4">
        <v-col cols="12" xs="12" sm="12" md="3" lg="1">
          <span class="label">Org name:</span>
        </v-col>
        <v-col cols="12" xs="12" sm="10" md="5" lg="4">
          <!-- System Admin org name cannot be changed -->
          <div style="font-size:1.2rem;" v-if="uiType === 'sys-admin'">
            {{ orgToEdit.name }}
          </div>
          <v-text-field
              v-else
              outlined
              hide-details
              :disabled="isOrgInfoSaving"
              :rules="$root.rules.fieldRequired"
              @input="setDirty('name')"
              v-model="orgToEdit.name" />
        </v-col>
      </v-row>

      <!-- Email -->
      <v-row align="center" class="pa-0 ma-0 mt-5">
        <v-col cols="12" xs="12" sm="12" md="3" lg="1">
          <span class="label">Email:</span>
        </v-col>
        <v-col cols="12" xs="12" sm="10" md="5" lg="4">
          <v-text-field
              outlined
              hide-details
              :disabled="isOrgInfoSaving"
              @input="setDirty('email')"
              v-model="orgToEdit.email" />
        </v-col>
      </v-row>
    </v-container>

  </v-container>

</template>

<script>
export default {
  name: 'OrgProfileEditPage',

  data () {
    return {
      // ------------ manage org info
      isDirty: false,
      dirtyProps: [],
      isOrgInfoSaving: false,
      orgInfoSaveErrorMessage: null,
      orgInfoSavedMessage: null
    }
  },

  computed: {
    uiType () {
      return this.$root.uiType()
    },
    activeOrg () {
      return this.$store.getters['auth/activeOrg']
    },
    orgToEdit () {
      return this.$store.getters['auth/activeOrgEditing']
    }
  },

  async beforeMount () {
    // Load active org to edit
    this.$store.dispatch('auth/setActiveOrgToEdit')
  },

  async beforeDestroy () {
    // Clear org editing
    this.$store.dispatch('auth/clearActiveOrgEditing')
  },

  methods: {
    // Display a formatted date
    formatDate (date) {
      return (date) ? this.$root.utils.format(date, 'MM/DD/YYYY') : ''
    },

    // Manage dirty state
    setDirty (propName) {
      if (this.activeOrg[propName] !== this.orgToEdit[propName]) {
        if (!this.$root.utils.includes(this.dirtyProps, propName)) this.dirtyProps.push(propName)
        this.isDirty = true
      } else {
        const dirtyPropIndex = this.dirtyProps.findIndex(prop => prop === propName)
        if (dirtyPropIndex !== -1) this.dirtyProps.splice(dirtyPropIndex, 1)
        if (this.dirtyProps.length === 0) this.isDirty = false
      }
    },

    // Cancel changes
    cancel () {
      this.$store.dispatch('auth/setActiveOrgToEdit') // reset user info
      this.dirtyProps = []
      this.isDirty = false
    },

    // Return to org profile page
    close () {
      this.$root.goTo('org-profile')
    },

    // Save org info changes
    async saveOrgInfo () {
      this.isOrgInfoSaving = true

      try {
        // Update org
        await this.$store.dispatch('auth/updateOrg')

        // Clear dirty
        this.dirtyProps = []
        this.isDirty = false

        // Flash saved message
        this.orgInfoSavedMessage = 'Changes Saved!'
        setTimeout(() => {
          this.orgInfoSavedMessage = null
        }, 4000)
      } catch (err) {
        this.orgInfoSaveErrorMessage = err.message || err.text || err
      } finally {
        this.isOrgInfoSaving = false
      }
    }
  }
}
</script>

<style>
  .page-org-profile-edit .changed-message-text {
    font-size: 1.0rem;
    color: #4CAF50;
    font-weight: 400;
  }

  .page-org-profile-edit .changed-message-text {
    font-size: 1.0rem;
    color: #4CAF50;
    font-weight: 400;
  }
</style>
